import * as React from 'react';
import { MainMenu, Slider, Layout, Footer } from '../components';

export default function Home() {
	return (
		<Layout>
			<MainMenu />
			<header>
				<div className="intro">
					<h1>Data Everywhere, Instant GraphQL</h1>
					<p>
						Graphweaver shortcuts your GraphQL development by securely connecting all your data
						sources in one place.
					</p>
				</div>

				<div className="hero">
					<img src="/img/hero-image.png" alt="Graphweaver" />
				</div>
			</header>
			<main>
				<section className="highlights">
					<div className="highlight">
						<img className="icon" src="/img/secure.svg" alt="Access data securely icon" />
						Secure
					</div>
					<div className="highlight">
						<img
							className="icon"
							src="/img/merge-multiple-sources.svg"
							alt="Merge multiple data sources icon"
						/>
						Multiple data sources
					</div>
					<div className="highlight">
						<img className="icon" src="/img/browse.svg" alt="Browse through data icon" />
						Admin Panel
					</div>
					<div className="highlight">
						<img className="icon" src="/img/open-source-code.svg" alt="Open source code icon" />
						100% Open Source
					</div>
				</section>
			</main>

			<Slider />

			<main className="side-padding">
				<section id="how-it-works">
					<div className="intro">
						<div className="title-wrapper">
							<p className="label">Instant GraphQL</p>
							<h2>Speed Up Development</h2>
						</div>
						<p>
							With the Graphweaver CLI tool, you can introspect a connected database and instantly
							create the Typescript code needed to connect. These Typescript files are then yours to
							extend with custom code for complete flexibility. Start the server to autogenerate
							your resolvers.
						</p>
					</div>

					<div className="cards">
						<div className="card">
							<p className="step">1</p>
							<h3>Code Generator</h3>
							<p>Sources can be REST API, GraphQL API, SQL databases or PostgreSQL</p>
						</div>

						<div className="card">
							<p className="step">2</p>
							<h3>Autogenerate Resolvers</h3>
							<p>Don't waste time writing CRUD resolvers for your data.</p>
						</div>

						<div className="card">
							<p className="step">3</p>
							<h3>Cross Source Filters</h3>
							<p>
								Need to filter the data? Graphweaver creates filters automatically for all the
								connected data sources.
							</p>
						</div>
					</div>
					<img
						src="/img/banner-how-it-works.png"
						height="auto"
						alt="How it works"
						className="cropped"
					/>
				</section>

				<section>
					<div className="intro">
						<div className="title-wrapper">
							<p className="label">Data Everywhere</p>
							<h2>Connect to ANY Data Source</h2>
						</div>
						<p>We have pre-built data connectors that you can start working with straight away:</p>
						<div className="brand-icons">
							<img src="/img/postgres.svg" height="80px" alt="PostgreSQL" title="PostgreSQL" />
							<img src="/img/mysql.svg" height="80px" alt="MySQL" title="MySQL" />
							<img src="/img/redis.svg" height="80px" alt="Redis" title="Redis" />
							<img src="/img/open-api.svg" height="80px" alt="Open API" title="Open API" />
							<img src="/img/salesforce.svg" height="80px" alt="Salesforce" title="Salesforce" />
							<img src="/img/graphql.svg" height="80px" alt="GraphQL" title="GraphQL" />
							<img src="/img/contentful.svg" height="50px" alt="Contentful" title="Contentful" />
							<img src="/img/xero.svg" height="50px" alt="Xero" title="Xero" />
							<img
								src="/img/dynamics.svg"
								height="50px"
								alt="Microsoft Dynamics"
								title="Microsoft Dynamics"
							/>
							<img src="/img/mailchimp.svg" height="50px" alt="Mailchimp" title="Mailchimp" />
							<img src="/img/eventbrite.svg" height="50px" alt="Eventbrite" title="Eventbrite" />
						</div>
					</div>
				</section>

				<section>
					<div className="intro">
						<div className="title-wrapper">
							<p className="label">Browse</p>
							<h2>Flexible Admin Panel</h2>
						</div>
						<p>
							View, create, edit, and delete data from any data source. The admin panel is a
							React.js app that you can extend with custom pages and fields. Giving you the
							flexibility to customise it to any project you are working on.
						</p>
						<div className="hero">
							<img src="/img/admin-ui.png" height="auto" alt="Graphweaver" />
						</div>
					</div>
				</section>
				<section>
					<div className="try-it-out-wrapper">
						<h2>Try it out</h2>
						<div className="buttons">
							<a
								className="download-btn source"
								href="https://github.com/exogee-technology/graphweaver"
								target="_blank"
								rel="noopener noreferrer"
								pirsch-event="try-it-out-github"
							>
								GitHub <span></span>
							</a>
							<a
								className="get-started-btn"
								href="https://demo.graphweaver.com"
								target="_blank"
								rel="noopener noreferrer"
								pirsch-event="try-it-out-demo"
							>
								Demo <span></span>
							</a>
						</div>
					</div>
				</section>

				<Footer />
			</main>
		</Layout>
	);
}

export const Head = () => (
	<>
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>Graphweaver - Data Everywhere, Instant GraphQL</title>

		<link rel="stylesheet" href="https://use.typekit.net/ipx5nyp.css" />

		<link rel="apple-touch-icon" sizes="57x57" href="/icon/apple-icon-57x57.png" />
		<link rel="apple-touch-icon" sizes="60x60" href="/icon/apple-icon-60x60.png" />
		<link rel="apple-touch-icon" sizes="72x72" href="/icon/apple-icon-72x72.png" />
		<link rel="apple-touch-icon" sizes="76x76" href="/icon/apple-icon-76x76.png" />
		<link rel="apple-touch-icon" sizes="114x114" href="/icon/apple-icon-114x114.png" />
		<link rel="apple-touch-icon" sizes="120x120" href="/icon/apple-icon-120x120.png" />
		<link rel="apple-touch-icon" sizes="144x144" href="/icon/apple-icon-144x144.png" />
		<link rel="apple-touch-icon" sizes="152x152" href="/icon/apple-icon-152x152.png" />
		<link rel="apple-touch-icon" sizes="180x180" href="/icon/apple-icon-180x180.png" />
		<link rel="icon" type="image/png" sizes="192x192" href="/icon/android-icon-192x192.png" />
		<link rel="icon" type="image/png" sizes="32x32" href="/icon/favicon-32x32.png" />
		<link rel="icon" type="image/png" sizes="96x96" href="/icon/favicon-96x96.png" />
		<link rel="icon" type="image/png" sizes="16x16" href="/icon/favicon-16x16.png" />
		<link rel="manifest" href="/manifest.json" />
		<meta name="msapplication-TileColor" content="#ffffff" />
		<meta name="msapplication-TileImage" content="/icon/ms-icon-144x144.png" />
	</>
);
